/*
  Mobile nav drawer — modal behaviour for a <details> panel.

  <details> gives us open/close state and a labelled, keyboard-operable summary
  for free, which is why it was chosen. What it does not give us — and what
  becomes mandatory the moment the panel is an opaque, full-viewport overlay —
  is everything else a modal owes the user:

    - Focus stayed in document order, so tabbing past the last drawer link moved
      focus onto product cards and filter controls sitting invisibly behind the
      panel. That is WCAG 2.2 SC 2.4.11 Focus Not Obscured, and 2.4.3.
    - The page scrolled underneath the open panel.
    - Escape did nothing.

  `inert` does the heavy lifting: it removes everything behind the panel from
  the tab order AND the accessibility tree in one declaration, which is both
  more correct and far less brittle than hand-rolling a focus trap. Applied to
  body's children rather than a hardcoded list of landmarks, so it keeps working
  if the page composition changes.

  Progressive enhancement: with JS off, the drawer still opens and closes. It is
  just a non-modal panel, which is what it already was.
*/
class CtlDrawer {
  constructor(details) {
    this.details = details;
    this.summary = details.querySelector('summary');
    this.panel = details.querySelector('.ctl-drawer__panel');
    this.locked = [];

    this.onToggle = this.onToggle.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);

    details.addEventListener('toggle', this.onToggle);

    /* Explicit close control inside the panel. The summary is still the toggle,
       but once the panel is a full-height slide-out the user is looking at the
       panel, not back at the hamburger behind it. */
    const closeBtn = details.querySelector('[data-ctl-drawer-close]');
    if (closeBtn) closeBtn.addEventListener('click', () => this.dismiss());
  }

  onToggle() {
    if (this.details.open) this.open();
    else this.close();
  }

  open() {
    /* Everything that is not the drawer's own ancestor chain goes inert. */
    this.locked = Array.prototype.filter.call(
      document.body.children,
      (el) => !el.contains(this.details) && el.tagName !== 'SCRIPT'
    );
    this.locked.forEach((el) => {
      el.inert = true;
    });

    this.scrollY = window.scrollY;
    document.body.style.top = `-${this.scrollY}px`;
    document.body.classList.add('ctl-scroll-locked');

    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('pointerdown', this.onPointerDown, true);

    const first = this.panel && this.panel.querySelector('a, button');
    if (first) first.focus();
  }

  close() {
    this.locked.forEach((el) => {
      el.inert = false;
    });
    this.locked = [];

    document.body.classList.remove('ctl-scroll-locked');
    document.body.style.top = '';
    /* Restore the position the lock took away, without a smooth-scroll jump. */
    window.scrollTo(0, this.scrollY || 0);

    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('pointerdown', this.onPointerDown, true);
  }

  dismiss() {
    this.details.open = false;
    if (this.summary) this.summary.focus();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.dismiss();
    }
  }

  onPointerDown(event) {
    if (!this.details.contains(event.target)) this.dismiss();
  }
}

document.querySelectorAll('details.ctl-drawer').forEach((el) => new CtlDrawer(el));

/*
  Mega menu.

  The panel is opened by CSS on :focus-within, and on :hover — but only where
  (hover: hover) is true. At >=990px a touch-only tablet still gets the desktop
  nav, and there that left nothing at all: the trigger is a real link, so a tap
  could only ever do what its href says and go to the page, and the panel was
  unreachable by any input the device actually has.

  So where the device reports it cannot hover, this script is the opener. The
  first activation of the trigger opens the panel instead of following the
  link; a second one follows it — what a nav item owning a submenu already does
  on iOS. The href is never touched, so with JS off, on middle-click and on
  open-in-new-tab it still goes to the page.

  The capability is read live from the same media query the CSS gates on, so
  exactly one opener is ever active, the two can't disagree, and a hybrid
  laptop that grows a mouse moves between them on its own.

  Escape also has to clear that state, not just move focus out of the item —
  :focus-within alone would hold an otherwise-closed panel open.
*/
const ctlMegaHover = window.matchMedia('(hover: hover)');
let ctlMegaOpenItem = null;

function ctlMegaOutside(event) {
  if (ctlMegaOpenItem && !ctlMegaOpenItem.contains(event.target)) ctlMegaClose();
}

function ctlMegaClose() {
  if (!ctlMegaOpenItem) return;
  const trigger = ctlMegaOpenItem.querySelector(':scope > .ctl-header__link');
  ctlMegaOpenItem.removeAttribute('data-mega-open');
  if (trigger) {
    trigger.setAttribute('aria-expanded', 'false');
    /* :focus-within would hold the panel open behind the cleared state. */
    if (document.activeElement === trigger) trigger.blur();
  }
  ctlMegaOpenItem = null;
  document.removeEventListener('pointerdown', ctlMegaOutside, true);
  document.removeEventListener('touchstart', ctlMegaOutside, true);
}

function ctlMegaOpen(item) {
  if (ctlMegaOpenItem === item) return;
  ctlMegaClose();
  ctlMegaOpenItem = item;
  item.setAttribute('data-mega-open', '');
  const trigger = item.querySelector(':scope > .ctl-header__link');
  /* Announced only while the script owns the state. Where hover opens the
     panel nothing updates it, and a permanently-"false" attribute would be
     a worse lie to a screen reader than no attribute at all. */
  if (trigger) trigger.setAttribute('aria-expanded', 'true');
  document.addEventListener('pointerdown', ctlMegaOutside, true);
  document.addEventListener('touchstart', ctlMegaOutside, true);
}

/* Capture: the per-item handler below stops propagation, so a bubble-phase
   listener on the document would never see Escape at all. */
document.addEventListener(
  'keydown',
  (event) => {
    if (event.key === 'Escape') ctlMegaClose();
  },
  true
);

document.querySelectorAll('.ctl-header__item--has-mega').forEach((item) => {
  const trigger = item.querySelector(':scope > .ctl-header__link');
  if (!trigger) return;

  item.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    trigger.focus();
    trigger.blur();
  });

  let startX = 0;
  let startY = 0;
  let moved = false;
  /* The tap that opened the panel must not also come back through as the
     click the browser synthesises from it and navigate anyway. */
  let swallowClickUntil = 0;

  trigger.addEventListener(
    'touchstart',
    (event) => {
      const touch = event.changedTouches && event.changedTouches[0];
      startX = touch ? touch.clientX : 0;
      startY = touch ? touch.clientY : 0;
      moved = false;
      /* The click synthesised from the previous tap has already been and gone
         by now, so a fresh touch ends the window rather than being swallowed
         by it — otherwise a quick second tap, the one that means "now take me
         to the page", is eaten by the first tap's timer. */
      swallowClickUntil = 0;
    },
    { passive: true }
  );

  trigger.addEventListener(
    'touchmove',
    (event) => {
      const touch = event.changedTouches && event.changedTouches[0];
      if (!touch) return;
      /* A scroll that happened to start on the trigger is not a tap. */
      if (Math.abs(touch.clientX - startX) > 10 || Math.abs(touch.clientY - startY) > 10) moved = true;
    },
    { passive: true }
  );

  trigger.addEventListener('touchend', (event) => {
    if (moved || ctlMegaHover.matches || ctlMegaOpenItem === item) return;
    event.preventDefault();
    ctlMegaOpen(item);
    swallowClickUntil = Date.now() + 500;
  });

  trigger.addEventListener('click', (event) => {
    if (Date.now() < swallowClickUntil) {
      event.preventDefault();
      return;
    }
    /* Where hover works the trigger stays an ordinary link, unchanged. */
    if (ctlMegaHover.matches || ctlMegaOpenItem === item) return;
    /* Modified clicks mean "open the page". */
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    /* detail 0 is a click the keyboard made, and focusing the trigger has
       already opened the panel via :focus-within — so Enter means the page,
       not another way to open what the user can see. */
    if (event.detail === 0) return;
    event.preventDefault();
    ctlMegaOpen(item);
  });
});

/*
  Cart trigger.

  Dawn ships <cart-drawer> and its JS, and the drawer already slides in from the
  right — but our header never had anything wired to open it, so the cart icon
  just navigated to /cart and the drawer only ever appeared after an add-to-cart.
  That was flagged in the audit as a dead feature we were paying for: the drawer
  markup, cart-drawer.js and five stylesheets loaded on every page for a control
  that did not exist.

  The anchor keeps its real href, so with JS off, or on middle-click and
  open-in-new-tab, it still goes to the cart page.
*/
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-ctl-cart-open]');
  if (!trigger) return;
  /* Let the browser handle modified clicks — they mean "open the page". */
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

  const drawer = document.querySelector('cart-drawer');
  if (!drawer || typeof drawer.open !== 'function') return;

  event.preventDefault();
  drawer.open(trigger);
});
